import React, { createContext, useContext, useState, useCallback } from "react";

export interface EventLeadRecord {
  id: string;
  brand_id: string;
  event_id: string;
  type: string;
  email: string;
  name: string | null;
  phone: string | null;
  notes: string | null;
  activity: string | null;
  metadata: Record<string, unknown> | null;
  status: string | null;
  created_at: string;
  updated_at: string;
  event?: { id: string; title: string };
}

export interface EventLeadFormData {
  event_id: string;
  email: string;
  name: string;
  phone: string;
  notes: string;
  activity: string;
  type: string;
  status: string;
}

type DialogMode = "add" | "edit";

interface EventLeadDialogContextType {
  isOpen: boolean;
  mode: DialogMode;
  lead: EventLeadRecord | null;
  formData: EventLeadFormData;
  openAdd: () => void;
  openEdit: (lead: EventLeadRecord) => void;
  closeDialog: () => void;
  updateFormData: (data: Partial<EventLeadFormData>) => void;
  resetForm: () => void;
  onSuccess?: () => void;
  setOnSuccess: (callback: (() => void) | undefined) => void;
}

const defaultFormData: EventLeadFormData = {
  event_id: "",
  email: "",
  name: "",
  phone: "",
  notes: "",
  activity: "",
  type: "waiting_list",
  status: "pending",
};

const EventLeadDialogContext = createContext<EventLeadDialogContextType | undefined>(undefined);

export const EventLeadDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<DialogMode>("add");
  const [lead, setLead] = useState<EventLeadRecord | null>(null);
  const [formData, setFormData] = useState<EventLeadFormData>(defaultFormData);
  const [onSuccess, setOnSuccess] = useState<(() => void) | undefined>(undefined);

  const resetForm = useCallback(() => {
    setFormData(defaultFormData);
  }, []);

  const openAdd = useCallback(() => {
    resetForm();
    setLead(null);
    setMode("add");
    setIsOpen(true);
  }, [resetForm]);

  const openEdit = useCallback((l: EventLeadRecord) => {
    setLead(l);
    setFormData({
      event_id: l.event_id,
      email: l.email,
      name: l.name || "",
      phone: l.phone || "",
      notes: l.notes || "",
      activity: l.activity || "",
      type: l.type,
      status: l.status || "pending",
    });
    setMode("edit");
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setLead(null);
      resetForm();
    }, 200);
  }, [resetForm]);

  const updateFormData = useCallback((data: Partial<EventLeadFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  return (
    <EventLeadDialogContext.Provider
      value={{
        isOpen,
        mode,
        lead,
        formData,
        openAdd,
        openEdit,
        closeDialog,
        updateFormData,
        resetForm,
        onSuccess,
        setOnSuccess,
      }}
    >
      {children}
    </EventLeadDialogContext.Provider>
  );
};

export const useEventLeadDialog = () => {
  const context = useContext(EventLeadDialogContext);
  if (context === undefined) {
    throw new Error("useEventLeadDialog must be used within EventLeadDialogProvider");
  }
  return context;
};
